import { createContext, type RefObject } from "react";
import type { Map as MapboxMap } from "mapbox-gl";
import type { PlaceGeoJSON } from "~/lib/api";
import type { FitBoundsPadding } from "~/lib/mapConstants";

export type FlyToOptions = {
  /** Target zoom level. Used when fitWith is empty or not provided. */
  zoom?: number;
  /** Additional points to fit in the viewport alongside the center. */
  fitWith?: [number, number][];
  /** Padding when fitting multiple points. */
  padding?: FitBoundsPadding;
};

export interface ViewportContextValue {
  /** Whether the map has finished loading and is ready for interactions */
  isReady: boolean;

  /**
   * Fly the map to a center point.
   * If `fitWith` has points, adjusts viewport to include all of them.
   * Otherwise, flies to center at the specified (or default) zoom level.
   */
  flyTo: (center: [number, number], options?: FlyToOptions) => void;

  /** Fit the map to bounds of a GeoJSON feature collection */
  fitBounds: (geojson: PlaceGeoJSON, padding?: FitBoundsPadding) => void;
}

/** Internal context for Canvas to access map ref and setIsReady */
export interface ViewportInternals {
  mapRef: RefObject<MapboxMap | null>;
  setIsReady: (ready: boolean) => void;
}

export const ViewportContext = createContext<ViewportContextValue | null>(null);
export const ViewportInternalsContext = createContext<ViewportInternals | null>(null);
